// User Table Component

import { renderPagination, initPagination } from './Pagination.js';

// Current sort state
let currentSortBy = 'created_at';
let currentSortOrder = 'desc';

/**
 * Render the user table
 * @param {Object} userData - User list data with pagination
 * @returns {string} HTML markup
 */
export function renderUserTable(userData) {
  if (!userData || !userData.items) {
    return '<p class="loading">Lade Benutzer...</p>';
  }

  if (userData.items.length === 0) {
    return renderEmptyState();
  }

  return `
    <div class="user-table-wrapper">
      <table class="user-table">
        <thead>
          <tr>
            ${renderSortableHeader('email', 'E-Mail')}
            ${renderSortableHeader('username', 'Benutzername')}
            <th>Provider</th>
            <th>Admin</th>
            ${renderSortableHeader('created_at', 'Registriert')}
            ${renderSortableHeader('last_login_at', 'Letzter Login')}
            ${renderSortableHeader('bookmark_count', 'Bookmarks')}
          </tr>
        </thead>
        <tbody>
          ${renderUserRows(userData.items)}
        </tbody>
      </table>
    </div>
    ${renderPagination({
      page: userData.page,
      pages: userData.pages,
      total: userData.total,
      size: userData.size
    })}
  `;
}

/**
 * Render a sortable column header
 * @param {string} column - Column name
 * @param {string} label - Display label
 * @returns {string} HTML markup
 */
function renderSortableHeader(column, label) {
  const isActive = currentSortBy === column;
  const arrow = isActive ? (currentSortOrder === 'asc' ? '↑' : '↓') : '';

  return `
    <th class="sortable ${isActive ? 'active' : ''}" data-sort="${column}">
      ${label} <span class="sort-indicator">${arrow}</span>
    </th>
  `;
}

/**
 * Render table rows for users
 * @param {Array} users - User list
 * @returns {string} HTML markup
 */
function renderUserRows(users) {
  return users.map(user => `
    <tr>
      <td class="email-cell" title="${escapeHtml(user.email)}">
        ${truncateEmail(user.email)}
      </td>
      <td>${escapeHtml(user.username || '-')}</td>
      <td>
        <span class="provider-badge provider-${user.oauth_provider}">
          ${escapeHtml(user.oauth_provider)}
        </span>
      </td>
      <td>${user.is_admin ? '<span class="admin-badge">Admin</span>' : '-'}</td>
      <td>${formatDate(user.created_at)}</td>
      <td>${formatRelativeDate(user.last_login_at)}</td>
      <td class="number-cell">${user.bookmark_count ?? 0}</td>
    </tr>
  `).join('');
}

/**
 * Render empty state
 * @returns {string} HTML markup
 */
function renderEmptyState() {
  return `
    <div class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <line x1="23" y1="11" x2="17" y2="11"></line>
      </svg>
      <p>Keine Benutzer vorhanden</p>
    </div>
  `;
}

/**
 * Initialize user table event handlers
 * @param {Object} userData - User data
 * @param {Function} onPageChange - Page change callback
 * @param {Function} onSortChange - Sort change callback
 */
export function initUserTable(userData, onPageChange, onSortChange) {
  // Initialize pagination
  initPagination(onPageChange);

  // Initialize sort headers
  document.querySelectorAll('.sortable').forEach(th => {
    th.addEventListener('click', () => {
      const column = th.dataset.sort;
      if (currentSortBy === column) {
        // Toggle direction
        currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        currentSortBy = column;
        currentSortOrder = 'desc';
      }
      onSortChange(currentSortBy, currentSortOrder);
    });
  });
}

/**
 * Update the user table with new data
 * @param {Object} userData - New user data
 * @param {Function} onPageChange - Page change callback
 * @param {Function} onSortChange - Sort change callback
 */
export function updateUserTable(userData, onPageChange, onSortChange) {
  const container = document.getElementById('user-table-container');
  if (container) {
    container.innerHTML = renderUserTable(userData);
    initUserTable(userData, onPageChange, onSortChange);
  }
}

/**
 * Truncate email address if too long
 * @param {string} email - Email address
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated email
 */
function truncateEmail(email, maxLength = 30) {
  if (!email) return '-';
  if (email.length <= maxLength) return escapeHtml(email);
  return escapeHtml(email.substring(0, maxLength - 3) + '...');
}

/**
 * Format date as localized string
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
}

/**
 * Format date as relative time (e.g., "vor 2 Tagen")
 * @param {string} dateStr - ISO date string
 * @returns {string} Relative time string
 */
function formatRelativeDate(dateStr) {
  if (!dateStr) return 'Nie';

  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    if (diffDays < 30) return `vor ${Math.floor(diffDays / 7)} Woche${Math.floor(diffDays / 7) > 1 ? 'n' : ''}`;

    return formatDate(dateStr);
  } catch {
    return '-';
  }
}

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
